import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaUserFriends, FaWallet, FaChartLine, FaUser } from "react-icons/fa";
import {
  FiHome,
  FiImage,
  FiFileText,
  FiPhone,
  FiUsers,
  FiMapPin,
  FiLink,
} from "react-icons/fi";
import { sendApiRequest } from "../../utils.js";

const AdminOverview = () => {
  const [count, setCount] = useState({});
  const [overviewStats, setOverviewStats] = useState({
    directStats: { active: 0, inactive: 0 },
    teamStats: { active: 0, inactive: 0 },
    businessStats: { self: 0, team: 0 },
    walletStats: { mainWallet: 0, totalIncome: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch admin counts
        const countsResponse = await sendApiRequest('api/admin/get_all_counts', {}, 'GET');
       
        setCount(countsResponse.data || {});

        // Fetch overview stats
        const statsResponse = await sendApiRequest('api/overview/stats', {}, 'GET');
      
        if (statsResponse.status===200) {
          setOverviewStats(statsResponse.data.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const cardData = [
    {
      icon: <FaUser />,
      label: "Direct Active/Inactive",
      value: `${overviewStats.directStats.active}/${overviewStats.directStats.inactive}`,
      bg: "bg-purple-600",
    },
    {
      icon: <FaUserFriends />,
      label: "Team Active/Inactive",
      value: `${overviewStats.teamStats.active}/${overviewStats.teamStats.inactive}`,
      bg: "bg-orange-500",
    },
    {
      icon: <FaChartLine />,
      label: "Business Self/Team",
      value: `${formatAmount(overviewStats.businessStats.self)}/${formatAmount(overviewStats.businessStats.team)}`,
      bg: "bg-red-600",
    },
    {
      icon: <FaWallet />,
      label: "Main Wallet/Total Income",
      value: `${formatAmount(overviewStats.walletStats.mainWallet)}/${formatAmount(overviewStats.walletStats.totalIncome)}`,
      bg: "bg-cyan-500",
    },
    {
      icon: <FiHome />,
      label: "Plots",
      value: `Available: ${count.plots || 0}`,
      bg: "bg-green-500",
    },
    {
      icon: <FiFileText />,
      label: "Plans",
      value: `${count.plans || 0} Active`,
      bg: "bg-blue-600",
    },
    {
      icon: <FiImage />,
      label: "Gallery",
      value: `${count.galaryItems || 0} Images`,
      bg: "bg-yellow-500",
    },
    {
      icon: <FiLink />,
      label: "News",
      value: `${count.newsItems || 0} Articles`,
      bg: "bg-red-500",
    },
    {
      icon: <FiMapPin />,
      label: "Legal",
      value: `${count.legalDocs || 0} Pending`,
      bg: "bg-indigo-600",
    },
    {
      icon: <FiPhone />,
      label: "Contact",
      value: `${count.contactMessages || 0} Requests`,
      bg: "bg-teal-600",
    },
    {
      icon: <FiUsers />,
      label: "Team Members",
      value: `${count.teamMembers || 0} Members`,
      bg: "bg-pink-600",
    },
  ];

  // Debug log to check current state

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 text-gray-800 p-6 font-sans flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800 p-6 font-sans">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-12">
            Dashboard Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cardData.map((card, i) => (
              <motion.div
                key={i}
                className={`p-6 text-white rounded-2xl shadow-xl ${card.bg} transform hover:scale-105 transition-transform duration-300`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="flex justify-between items-center mb-4">
                  <div className="text-4xl">{card.icon}</div>
                </div>
                <div className="text-xl font-semibold mb-2">{card.label}</div>
                <div className="text-sm font-extrabold">{card.value}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
